// Pure payout-period math for the manager-facing reconciliation screen.
// No side effects, no UI — (estimates + members + effective goalsRewards +
// period) → per-advisor computed rows with estimate-level proof lines.
// Mirrors the advisor wallet's rules exactly (same VIN dedupe, same
// per-scan/per-paid settlement moments) so the number a manager approves
// is the number the advisor saw accrue.

import type {
  ApiEstimate,
  DealershipGoalsRewards,
  PayoutCadence,
  TeamMemberSummary,
} from "@/lib/api/types"
import { computeBonusEvents, computePayoutWindow, dateKey } from "@/lib/dashboard/advisor-stats"
import { computeCompetitionState } from "@/lib/dashboard/competition-state"
import { Roles } from "@/lib/team/roles"

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

export interface PayoutPeriod {
  start: Date
  // Inclusive last instant of the period (next period's start − 1ms) — the
  // same convention the mock seed stamps on PayoutRecord.periodEnd, so
  // ISO-string round-trips match records exactly.
  end: Date
  label: string
  closed: boolean
}

/**
 * Current payout period + the `pastCount` periods before it, newest first
 * (index 0 = the still-open current period). Anchors match
 * computePayoutWindow: weekly Mon-start, biweekly anchored to Mon
 * 2024-01-01, monthly 1st.
 */
export function enumeratePayoutPeriods(
  cadence: PayoutCadence,
  now: Date,
  pastCount: number,
): PayoutPeriod[] {
  const { periodStart, nextPayout } = computePayoutWindow(now, cadence)
  const stepDays = cadence === "weekly" ? 7 : cadence === "biweekly" ? 14 : 0

  const starts: Date[] = []
  for (let i = 0; i <= pastCount; i++) {
    if (cadence === "monthly") {
      starts.push(new Date(periodStart.getFullYear(), periodStart.getMonth() - i, 1))
    } else {
      const s = new Date(periodStart)
      s.setDate(s.getDate() - stepDays * i)
      starts.push(s)
    }
  }

  return starts.map((start, i) => {
    const nextStart = i === 0 ? nextPayout : starts[i - 1]
    const end = new Date(nextStart.getTime() - 1)
    return {
      start,
      end,
      label: periodLabel(cadence, start, end),
      closed: i > 0,
    }
  })
}

function periodLabel(cadence: PayoutCadence, start: Date, end: Date): string {
  if (cadence === "monthly") {
    return `${MONTH_LONG[start.getMonth()]} ${start.getFullYear()}`
  }
  const fmt = (d: Date) => `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`
}

/** yyyy-mm-dd of the period start — CSV filenames and stable react keys. */
export function periodKey(period: PayoutPeriod): string {
  return dateKey(period.start)
}

export type PayoutProofKind = "scan" | "completed" | "competition" | "bonus"

// One estimate-level (or win-level) line of evidence behind a computed
// amount — what the manager expands a row to audit.
export interface PayoutProofLine {
  date: Date
  label: string
  vehicleLabel?: string
  amount: number
  kind: PayoutProofKind
  // Per-scan $ suppressed by the 30-day same-VIN dedupe — the scan still
  // counts, only the $ is withheld. UI tags these "duplicate — no payout."
  duplicateOfVin?: boolean
}

export interface PayoutAdvisorRow {
  userId: number
  name: string
  scans: number
  scheduled: number
  completed: number
  declined: number
  // Estimates carrying a metadata.deferral marker — 0 until deferral ships.
  deferredCount: number
  baseScanPay: number
  completedPay: number
  // Activation/goal-hit/streak bonuses whose earned-at date falls in this
  // period. Built from the SAME computeBonusEvents helper the advisor wallet
  // uses, so the manager approves exactly what the advisor saw accrue (req
  // 2.2.3). Best-effort timing carries over: streak milestones date to `now`
  // (the open period); goal-hits date to their week/month end.
  bonusPay: number
  competitionPay: number
  computedTotal: number
  events: PayoutProofLine[]
}

interface TeamPayoutsInput {
  estimates: ApiEstimate[]
  members: TeamMemberSummary[] | undefined
  // Effective config — caller merges via withGoalsRewardsDefaults.
  goalsRewards: DealershipGoalsRewards
  // Advisor count driving per-advisor goal targets — must match the value
  // the wallet uses (resolveActiveAdvisors) so bonus math reconciles.
  activeAdvisorCount: number
  period: { start: Date; end: Date }
  now?: Date
}

export function computeTeamPayouts(input: TeamPayoutsInput): PayoutAdvisorRow[] {
  const now = input.now ?? new Date()
  const startMs = input.period.start.getTime()
  const endMs = input.period.end.getTime()
  const inPeriod = (d: Date) => {
    const t = d.getTime()
    return Number.isFinite(t) && t >= startMs && t <= endMs
  }

  const advisors = (input.members ?? []).filter((m) => m.role === Roles.ServiceAdvisor)
  const rowByUser = new Map<number, PayoutAdvisorRow>()
  for (const m of advisors) {
    rowByUser.set(m.id, {
      userId: m.id,
      name: `${m.firstName} ${m.lastName}`.trim(),
      scans: 0,
      scheduled: 0,
      completed: 0,
      declined: 0,
      deferredCount: 0,
      baseScanPay: 0,
      completedPay: 0,
      bonusPay: 0,
      competitionPay: 0,
      computedTotal: 0,
      events: [],
    })
  }

  const rewards = input.goalsRewards.rewards ?? {}
  const perScan = rewards.perEstimateSubmitted?.monetary?.enabled
    ? rewards.perEstimateSubmitted.monetary.amount ?? 0
    : 0
  const perPaid = rewards.perEstimateCompleted?.monetary?.enabled
    ? rewards.perEstimateCompleted.monetary.amount ?? 0
    : 0

  // VIN payout dedupe — identical walk to advisor-stats: per-scan $ fires
  // only for the FIRST estimate on a VIN within a trailing 30 days,
  // team-wide, in createdAt order; a paying scan re-arms its VIN's window.
  const VIN_DEDUPE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
  const duplicateVinIds = new Set<number>()
  {
    const ordered = input.estimates
      .map((e) => ({ e, at: new Date(e.createdAt).getTime() }))
      .filter((x) => Number.isFinite(x.at))
      .sort((a, b) => a.at - b.at)
    const lastPaidByVin = new Map<string, number>()
    for (const { e, at } of ordered) {
      const vin = e.vehicle?.vin
      if (!vin) continue
      const lastPaid = lastPaidByVin.get(vin)
      if (lastPaid != null && at - lastPaid < VIN_DEDUPE_WINDOW_MS) {
        duplicateVinIds.add(e.id)
      } else {
        lastPaidByVin.set(vin, at)
      }
    }
  }

  const vehicleLabel = (e: ApiEstimate): string | undefined => {
    const label = [e.vehicle?.year, e.vehicle?.make, e.vehicle?.model]
      .filter(Boolean)
      .join(" ")
    return label || undefined
  }

  for (const e of input.estimates) {
    const uid = e.createdBy?.id
    if (uid == null) continue
    const row = rowByUser.get(uid)
    if (!row) continue

    const createdAt = new Date(e.createdAt)
    if (inPeriod(createdAt)) {
      row.scans++
      if (e.status === "Scheduled") row.scheduled++
      if (e.status === "Declined") row.declined++
      if (e.metadata?.deferral != null) row.deferredCount++
      const isDup = duplicateVinIds.has(e.id)
      if (perScan > 0) {
        if (!isDup) row.baseScanPay += perScan
        row.events.push({
          date: createdAt,
          label: `Estimate #${e.id} saved`,
          vehicleLabel: vehicleLabel(e),
          amount: isDup ? 0 : perScan,
          kind: "scan",
          ...(isDup ? { duplicateOfVin: true } : {}),
        })
      }
    }

    // Per-paid settles the moment the estimate is Completed (updatedAt),
    // matching the wallet — so a scan from last period that completes this
    // period pays out in THIS period.
    if (perPaid > 0 && e.status === "Completed") {
      const updatedAt = new Date(e.updatedAt)
      const paidAt = Number.isFinite(updatedAt.getTime()) ? updatedAt : createdAt
      if (inPeriod(paidAt)) {
        row.completed++
        row.completedPay += perPaid
        row.events.push({
          date: paidAt,
          label: `Estimate #${e.id} paid`,
          vehicleLabel: vehicleLabel(e),
          amount: perPaid,
          kind: "completed",
        })
      }
    }
  }

  // Competition wins with wonAt inside the period. horizonStart widens
  // competition-state's default trailing-8-week window back to the visible
  // period so older navigable periods still resolve their wins (finding 2).
  const competitionState = computeCompetitionState({
    estimates: input.estimates,
    members: input.members,
    competitions: input.goalsRewards.competitions,
    currentUserId: null,
    horizonStart: input.period.start,
    now,
  })
  for (const win of competitionState.wins) {
    const row = rowByUser.get(win.userId)
    if (!row || !inPeriod(win.wonAt)) continue
    row.competitionPay += win.amount
    row.events.push({
      date: win.wonAt,
      label: win.label,
      amount: win.amount,
      kind: "competition",
    })
  }

  // Bonus events per advisor — same helper the wallet uses, filtered to this
  // period by earned-at date (finding 9). Groups each advisor's estimates
  // once, then attributes their in-period bonus events.
  const mineByUser = new Map<number, ApiEstimate[]>()
  for (const e of input.estimates) {
    const uid = e.createdBy?.id
    if (uid == null || !rowByUser.has(uid)) continue
    const list = mineByUser.get(uid)
    if (list) list.push(e)
    else mineByUser.set(uid, [e])
  }
  for (const [uid, row] of rowByUser) {
    const mine = mineByUser.get(uid) ?? []
    if (mine.length === 0) continue
    const bonusEvents = computeBonusEvents(
      mine,
      input.goalsRewards,
      input.activeAdvisorCount,
      now,
      uid,
    )
    for (const ev of bonusEvents) {
      if (!inPeriod(ev.occurredAt)) continue
      row.bonusPay += ev.amount
      row.events.push({
        date: ev.occurredAt,
        label: ev.label,
        amount: ev.amount,
        kind: "bonus",
      })
    }
  }

  const rows = [...rowByUser.values()]
  for (const row of rows) {
    row.computedTotal =
      row.baseScanPay + row.completedPay + row.bonusPay + row.competitionPay
    row.events.sort((a, b) => a.date.getTime() - b.date.getTime())
  }
  rows.sort((a, b) => a.name.localeCompare(b.name))
  return rows
}
